import React, { useEffect, useState } from "react";
import { NavBar, SideNav } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, TextInput, Label, Badge, Spinner, Table } from "flowbite-react";
import {
  fetchReferralCodes,
  createReferralCode,
  updateReferralCode,
  deleteReferralCode,
} from "../../redux/slices/referralCodes";

const Ambassadors = () => {
  const dispatch = useDispatch();
  const { codes, pageLoading, actionLoading } = useSelector(
    (state) => state.referralCodes
  );

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [ambassadorId, setAmbassadorId] = useState("");
  const [customCode, setCustomCode] = useState("");
  const [deleteConfirmCode, setDeleteConfirmCode] = useState(null);

  useEffect(() => {
    dispatch(fetchReferralCodes());
  }, [dispatch]);

  const handleCreate = () => {
    if (!ambassadorId.trim()) return;
    dispatch(
      createReferralCode({
        ambassadorId: ambassadorId.trim(),
        customCode: customCode.trim() || undefined,
      })
    ).then((res) => {
      if (!res.error) {
        setShowCreateModal(false);
        setAmbassadorId("");
        setCustomCode("");
      }
    });
  };

  const handleToggleActive = (code, isActive) => {
    dispatch(updateReferralCode({ code, isActive: !isActive }));
  };

  const handleDelete = (code) => {
    dispatch(deleteReferralCode(code)).then(() => setDeleteConfirmCode(null));
  };

  return (
    <div>
      <NavBar />
      <SideNav />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Creators
          </h1>
          <Button onClick={() => setShowCreateModal(true)}>
            + Create Referral Code
          </Button>
        </div>

        {pageLoading ? (
          <div className="flex justify-center mt-20">
            <Spinner size="xl" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table hoverable>
              <Table.Head>
                <Table.HeadCell>Code</Table.HeadCell>
                <Table.HeadCell>Creator</Table.HeadCell>
                <Table.HeadCell>Uses</Table.HeadCell>
                <Table.HeadCell>Status</Table.HeadCell>
                <Table.HeadCell>Created</Table.HeadCell>
                <Table.HeadCell>Actions</Table.HeadCell>
              </Table.Head>
              <Table.Body>
                {codes.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan={6} className="text-center text-gray-400 py-8">
                      No referral codes yet
                    </Table.Cell>
                  </Table.Row>
                )}
                {codes.map((item) => (
                  <Table.Row key={item.code} className="bg-white dark:bg-gray-800">
                    <Table.Cell className="font-mono font-semibold text-purple-700">
                      {item.code}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {item.ambassadorDisplayName || "—"}
                      </div>
                      <div className="text-xs text-gray-500">{item.ambassadorEmail}</div>
                    </Table.Cell>
                    <Table.Cell>{item.usageCount}</Table.Cell>
                    <Table.Cell>
                      <Badge color={item.isActive ? "success" : "gray"}>
                        {item.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell className="text-xs text-gray-500">
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString()
                        : "—"}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex gap-2">
                        <Button
                          size="xs"
                          color={item.isActive ? "gray" : "success"}
                          onClick={() => handleToggleActive(item.code, item.isActive)}
                          disabled={actionLoading}
                        >
                          {item.isActive ? "Disable" : "Enable"}
                        </Button>
                        {item.usageCount === 0 && (
                          <Button
                            size="xs"
                            color="failure"
                            onClick={() => setDeleteConfirmCode(item.code)}
                            disabled={actionLoading}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}

        {/* Create Code Modal */}
        <Modal show={showCreateModal} onClose={() => { setShowCreateModal(false); setCustomCode(""); }}>
          <Modal.Header>Create Referral Code</Modal.Header>
          <Modal.Body>
            <div className="space-y-4">
              <div>
                <Label htmlFor="ambassadorId">Creator User ID *</Label>
                <TextInput
                  id="ambassadorId"
                  placeholder="Firebase Auth UID"
                  value={ambassadorId}
                  onChange={(e) => setAmbassadorId(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="customCode">
                  Custom Code (optional)
                </Label>
                <TextInput
                  id="customCode"
                  placeholder="e.g. EMMA-VIP or leave blank to auto-generate"
                  value={customCode}
                  onChange={(e) => setCustomCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
                  maxLength={30}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Letters, numbers and hyphens only. If blank, a code like <span className="font-mono">EMMA-A3B5C9D2</span> is auto-generated from the user's name.
                </p>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={handleCreate} disabled={actionLoading || !ambassadorId.trim()}>
              {actionLoading ? <Spinner size="sm" /> : "Create"}
            </Button>
            <Button color="gray" onClick={() => { setShowCreateModal(false); setCustomCode(""); }}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirm Modal */}
        <Modal show={!!deleteConfirmCode} onClose={() => setDeleteConfirmCode(null)}>
          <Modal.Header>Delete Referral Code</Modal.Header>
          <Modal.Body>
            <p>
              Are you sure you want to delete{" "}
              <span className="font-mono font-bold">{deleteConfirmCode}</span>? This
              cannot be undone.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              color="failure"
              onClick={() => handleDelete(deleteConfirmCode)}
              disabled={actionLoading}
            >
              {actionLoading ? <Spinner size="sm" /> : "Delete"}
            </Button>
            <Button color="gray" onClick={() => setDeleteConfirmCode(null)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      </main>
    </div>
  );
};

export default Ambassadors;
